import { LitElement, html, svg, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { UWidgetSpec } from '../core/types.js';
import { themeStyles } from '../styles/tokens.js';
import { formatValue } from '../core/format.js';

interface GaugeThreshold {
  to: number;
  color: string;
  label?: string;
}

interface GaugeOptions {
  min: number;
  max: number;
  unit: string;
  subtitle: string;
  thresholds: GaugeThreshold[];
  /** Format hint for the shown value (`formatValue`) — default `number`, in the widget's locale. */
  format?: string;
  locale?: string;
}

const DEFAULT_GAUGE_OPTIONS: GaugeOptions = {
  min: 0,
  max: 100,
  unit: '',
  subtitle: '',
  thresholds: [],
};

const COLOR_MAP: Record<string, string> = {
  green: '#16a34a',
  yellow: '#eab308',
  orange: '#f97316',
  red: '#dc2626',
  blue: '#2563eb',
  gray: '#6b7280',
};

function resolveColor(color: string): string {
  return COLOR_MAP[color] ?? color;
}

/** Width (viewBox units) the centre text may take — the inner span of the arc. */
const CENTER_TEXT_WIDTH = 130;

/**
 * Centre lines stacked around the arc centre (100,100), in viewBox units. Heights are each line's
 * font size × 1.2 at the default theme sizes, so the stack sits where the HTML overlay used to.
 */
const VALUE_LINE = 42;
const UNIT_LINE = 18;
const SUBTITLE_LINE = 20;

// SVG arc path helper
function describeArc(cx: number, cy: number, r: number, startDeg: number, endDeg: number): string {
  const startRad = ((startDeg - 90) * Math.PI) / 180;
  const endRad = ((endDeg - 90) * Math.PI) / 180;
  const x1 = cx + r * Math.cos(startRad);
  const y1 = cy + r * Math.sin(startRad);
  const x2 = cx + r * Math.cos(endRad);
  const y2 = cy + r * Math.sin(endRad);
  const largeArc = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
}

@customElement('uw-gauge')
export class UwGauge extends LitElement {
  static styles = [themeStyles, css`
    







    :host {
      display: flex;
      flex-direction: column;
      font-family: var(--u-widget-font-family);
      container: uw-gauge / inline-size;
    }

    .gauge-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
    }

    



    .gauge-svg {
      display: block;
      flex: 0 1 auto;
      min-height: 0;
      width: auto;
      height: auto;
      max-width: min(100%, var(--u-widget-gauge-size, 160px));
    }

    .gauge-track {
      stroke: var(--u-widget-border, #e2e8f0);
    }

    

    .gauge-value {
      font-size: calc(1.75rem * 1.25);
      font-weight: 700;
      fill: var(--u-widget-text, #1a1a2e);
    }

    .gauge-unit {
      font-size: calc(var(--u-widget-font-size-caption, 0.75rem) * 1.25);
      fill: var(--u-widget-text-secondary, #5b6777);
    }

    .gauge-subtitle {
      font-size: calc(var(--u-widget-font-size-overline, 0.6875rem) * 1.25);
      font-weight: 600;
    }

    
    .progress-container {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .progress-bar-track {
      width: 100%;
      height: 8px;
      background: var(--u-widget-border, #e2e8f0);
      border-radius: 4px;
      overflow: hidden;
    }

    .progress-bar-fill {
      height: 100%;
      border-radius: 4px;
      transition: width var(--u-widget-duration-slow, 300ms) ease;
    }

    .progress-info {
      display: flex;
      justify-content: space-between;
      font-size: var(--u-widget-font-size-label, 0.8125rem);
      color: var(--u-widget-text-secondary, #5b6777);
    }

    
    @container uw-gauge (max-width: 10rem) {
      .progress-info {
        font-size: var(--u-widget-font-size-caption, 0.75rem);
      }

      .progress-bar-track {
        height: 6px;
      }
    }
  `];

  @property({ type: Object })
  spec: UWidgetSpec | null = null;

  @property({ type: String, reflect: true })
  theme: string | null = null;

  render() {
    if (!this.spec?.data) return nothing;

    if (this.spec.widget === 'progress') {
      return this.renderProgress();
    }

    return this.renderGauge();
  }

  private getOptions(): GaugeOptions {
    const opts = (this.spec!.options ?? {}) as Partial<GaugeOptions>;
    return { ...DEFAULT_GAUGE_OPTIONS, ...opts };
  }

  private getValue(): number {
    const data = this.spec!.data as Record<string, unknown>;
    return Number(data.value ?? 0);
  }

  private getActiveLabel(value: number, opts: GaugeOptions): string {
    if (opts.subtitle) return opts.subtitle;
    if (!opts.thresholds?.length) return '';
    const sorted = [...opts.thresholds].sort((a, b) => a.to - b.to);
    for (const t of sorted) {
      if (value <= t.to && t.label) return t.label;
    }
    const last = sorted[sorted.length - 1];
    return last.label ?? '';
  }

  private renderGauge() {
    const opts = this.getOptions();
    const value = this.getValue();
    const range = opts.max - opts.min;
    const pct = range > 0 ? Math.max(0, Math.min(1, (value - opts.min) / range)) : 0;
    const color = this.getThresholdColor(value, opts);
    const activeLabel = this.getActiveLabel(value, opts);

    const cx = 100, cy = 100, r = 80;
    const startAngle = 150;
    const totalAngle = 240;
    const endAngle = startAngle + totalAngle * pct;

    const trackPath = describeArc(cx, cy, r, startAngle, startAngle + totalAngle);
    const fillPath = pct > 0 ? describeArc(cx, cy, r, startAngle, endAngle) : null;

    const label = typeof this.spec!.title === 'string' ? this.spec!.title : 'Gauge';
    const shown = this.formatShown(value, opts);
    const valueText = activeLabel ? `${shown}${opts.unit} ${activeLabel}` : `${shown}${opts.unit}`;

    const lines: { kind: 'value' | 'unit' | 'subtitle'; text: string; height: number }[] = [
      { kind: 'value', text: shown, height: VALUE_LINE },
    ];
    if (opts.unit) lines.push({ kind: 'unit', text: opts.unit, height: UNIT_LINE });
    if (activeLabel) lines.push({ kind: 'subtitle', text: activeLabel, height: SUBTITLE_LINE });
    let top = cy - lines.reduce((sum, l) => sum + l.height, 0) / 2;
    const texts = lines.map((l) => {
      const y = top + l.height / 2;
      top += l.height;
      // The subtitle's text is written in updated(), which may shorten it — Lit owns no part inside it.
      return l.kind === 'subtitle'
        ? svg`<text class="gauge-subtitle" part="subtitle" x=${cx} y=${y} text-anchor="middle"
            dominant-baseline="central" fill=${color}></text>`
        : svg`<text class="gauge-${l.kind}" part=${l.kind} x=${cx} y=${y} text-anchor="middle"
            dominant-baseline="central">${l.text}</text>`;
    });
    this._subtitle = activeLabel;

    return html`
      <div class="gauge-container" part="gauge"
        role="meter"
        aria-valuenow=${value}
        aria-valuemin=${opts.min}
        aria-valuemax=${opts.max}
        aria-label=${label}
        aria-valuetext=${valueText}
      >
        <svg class="gauge-svg" width="200" height="195" viewBox="0 0 200 195" role="presentation" aria-hidden="true">
          <path class="gauge-track" d="${trackPath}" fill="none" stroke-width="12" stroke-linecap="round"></path>
          <path class="gauge-fill" d="${fillPath ?? 'M0 0'}" fill="none" stroke="${fillPath ? color : 'none'}" stroke-width="12" stroke-linecap="round"></path>
          ${texts}
        </svg>
      </div>
    `;
  }

  /** The gauge's centre label, written into its `<text>` after each render. */
  private _subtitle = '';

  private _resizeObserver?: ResizeObserver;

  connectedCallback() {
    super.connectedCallback();
    // Measuring needs the label laid out in its real font. A gauge first rendered hidden measures 0, and
    // one rendered before a web font arrives measures the fallback — measure again once it is shown
    // (its size changes from nothing) and once the page's fonts are ready.
    if (typeof ResizeObserver === 'function') {
      this._resizeObserver = new ResizeObserver(() => this._fitSubtitle());
      this._resizeObserver.observe(this);
    }
    document.fonts?.ready.then(() => this._fitSubtitle(), () => {});
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._resizeObserver?.disconnect();
    this._resizeObserver = undefined;
  }

  protected updated() {
    this._fitSubtitle();
  }

  /** SVG text has no text-overflow, so a label wider than the arc's inner span is cut with an ellipsis here. */
  private _fitSubtitle() {
    const subtitle = this.renderRoot.querySelector<SVGTextElement>('text.gauge-subtitle');
    if (!subtitle) return;
    const full = this._subtitle;
    subtitle.textContent = full;
    if (typeof subtitle.getComputedTextLength !== 'function' || subtitle.getComputedTextLength() <= CENTER_TEXT_WIDTH) return;
    let lo = 0;
    let hi = full.length;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      subtitle.textContent = `${full.slice(0, mid)}…`;
      if (subtitle.getComputedTextLength() <= CENTER_TEXT_WIDTH) lo = mid;
      else hi = mid - 1;
    }
    subtitle.textContent = `${full.slice(0, lo).trimEnd()}…`;
  }

  private renderProgress() {
    const data = this.spec!.data as Record<string, unknown>;
    const value = Number(data.value ?? 0);
    const max = Number(data.max ?? (this.spec!.options as Record<string, unknown>)?.max ?? 100);
    const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
    const opts = this.getOptions();
    const color = this.getThresholdColor(value, opts);
    const label = this.formatLabel(value, pct);

    const ariaLabel = typeof this.spec!.title === 'string' ? this.spec!.title : 'Progress';

    return html`
      <div class="progress-container" part="progress"
        role="progressbar"
        aria-valuenow=${value}
        aria-valuemin=${0}
        aria-valuemax=${max}
        aria-label=${ariaLabel}
      >
        <div class="progress-bar-track">
          <div
            class="progress-bar-fill"
            style="width:${pct}%; background:${color || 'var(--u-widget-primary, #4f46e5)'}"
            part="progress-fill"
          ></div>
        </div>
        <div class="progress-info">
          <span>${label}</span>
          <span>${Math.round(pct)}%</span>
        </div>
      </div>
    `;
  }

  /** The value as shown — in the widget's locale (`79,17` on a German page), through `options.format` (default `number`). */
  private formatShown(value: number, opts: GaugeOptions): string {
    return formatValue(value, opts.format ?? 'number', typeof opts.locale === 'string' ? opts.locale : undefined);
  }

  private formatLabel(value: number, pct: number): string {
    const opts = this.getOptions();
    const shown = this.formatShown(value, opts);
    const template = (this.spec!.options as Record<string, unknown> | undefined)?.label;
    if (template) {
      return String(template)
        .replace('{value}', shown)
        .replace('{percent}', String(Math.round(pct)));
    }
    return shown;
  }

  private getThresholdColor(value: number, opts: GaugeOptions): string {
    if (!opts.thresholds?.length) return 'var(--u-widget-primary, #4f46e5)';

    const sorted = [...opts.thresholds].sort((a, b) => a.to - b.to);
    for (const t of sorted) {
      if (value <= t.to) return resolveColor(t.color);
    }
    return resolveColor(sorted[sorted.length - 1].color);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'uw-gauge': UwGauge;
  }
}
